import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import NewsService from '@/services/CmsServices/NewsService';
import { extractError } from '@/helpers/extractApiError';
import { replaceStatusChangedItem, removeSelectedItem } from '@/helpers/CRUD';
import { deleteNewsArticle } from '../delete/deleteSlice';

const model = {
    data: [],
    totalItems: 0,
    loading: false,
    error: null,
    successOnNewsStatusChange: '',
};

export const getNewsList = createAsyncThunk(
    'news/getNewsList',
    async (data, { rejectWithValue }) => {
        try {
            const response = await NewsService.getNewsList(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const changeNewsStatus = createAsyncThunk(
    'news/changeNewsStatus',
    async (data, { rejectWithValue }) => {
        try {
            const response = await NewsService.changeNewsStatus(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const generateNewsUrlWithToken = createAsyncThunk(
    'news/generateNewsUrlWithToken',
    async (data, { rejectWithValue }) => {
        try {
            const response = await NewsService.generateNewsUrlWithToken(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const newsListSlice = createSlice({
    name: 'newsList',
    initialState: model,
    reducers: {
        resetChangeNewsStatusStateOnSuccess: (state) => {
            state.successOnNewsStatusChange = '';
        },
        resetChangeNewsStatusStateOnError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // news list
        builder.addCase(getNewsList.pending, (state) => {
            state.loading = true;
        }).addCase(getNewsList.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload.news;
            state.totalItems = action.payload.total;
        }).addCase(getNewsList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // change news status
        builder.addCase(changeNewsStatus.pending, (state) => {
            state.loading = true;
        }).addCase(changeNewsStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.successOnNewsStatusChange = 'News status changed successfully!';
            state.data = replaceStatusChangedItem(
                state.data,
                action.payload.id,
                action.payload.status,
            );
        }).addCase(changeNewsStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // generate news URL with token
        builder.addCase(generateNewsUrlWithToken.pending, (state) => {
            state.loading = true;
        }).addCase(generateNewsUrlWithToken.fulfilled, (state, action) => {
            state.loading = false;
            window.open(action.payload.preview_url, '_blank');
        }).addCase(generateNewsUrlWithToken.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Handle deleting news article
        builder.addCase(deleteNewsArticle.fulfilled, (state, action) => {
            state.data = removeSelectedItem(state.data, action.payload.newsArticleId);
            state.totalItems -= 1;
        });
    },
});

export const {
    resetChangeNewsStatusStateOnSuccess,
    resetChangeNewsStatusStateOnError,
} = newsListSlice.actions;

export default newsListSlice.reducer;
